import React, {useEffect, useState} from "react";
import "./RetailProcurement.scss";
import {useSelector} from "react-redux";
import {Chart as ChartJS, registerables} from "chart.js";
import {Client, isEmpty, Model} from "weed-js";
import {InventoryRecommendation} from "../../../../../../models/lab";
import {Table} from "../../../../../../components/table/Table.js";
import {ColumnDef} from "../../../../../../components/table/classes/ColumnDef";
import {DutchieAccount, Package, Brand, Product, Inventory} from "../../../../../../models/dutchie.js";
import {
    Brand as MetrcBrand,
    Package as MetrcPackage,
    PackageWholesalePrice as MetrcPackageWholesalePrice,
    Product as MetrcProduct,
    InventoryItem as MetrcInventory
} from "../../../../../../models/metrc.js";
import ProductSummary from "../ProductDetail/ProductSummary";
import ProductComparison from "./ProductComparison";
import Loading from "../../../../../../components/elements/Loading";
import {DUTCHIE, METRC} from "../../../../../../constants";
import {CanonicalIntegrationAccount, CanonicalProduct, CanonicalInventory} from "../../../../../../models/canonical";


ChartJS.register(...registerables);


const InventoryRecommendations = ({inventoryRecommendations, columnDefs, client}) => {
    return <>
        <h2>Inventory Recommendations</h2>
        <Table
            data={inventoryRecommendations.map(ir => ir.toRow()).filter(row => row !== null)}
            columnDefs={columnDefs}
            onViewWindowChange={(win) => {
                const promises = win.map(row => {
                    const invId = row.cells[0].getSortValue();
                    const recommendation = InventoryRecommendation.byId[invId];
                    if (!recommendation?.viewed && recommendation) {
                        recommendation.viewed = true;
                        return recommendation.save(client);
                    }
                    return null;
                }).filter(item => {
                    return item !== null;
                });
                if (isEmpty(promises)) {
                    return;
                }
                // Don't report anything to the user here. This is on us to resolve if there's an issue:
                Promise.all(promises).then(updates => {}).catch(err =>{
                    console.error("Error updating inventory recommendations!!", err);
                });
            }}
        />
    </>
}


const fetchDutchieData = (client, dutchieAccountIds, shouldCache, abortController) => {
    return Promise.all([
        DutchieAccount.fetch({
            client,
            params: {
                id__in: dutchieAccountIds,
            },
            abortController: abortController,
            paginate: false,
            cache: shouldCache
        }),
        InventoryRecommendation.fetch({
            client,
            params: {
                integration: DUTCHIE,
                account_id__in: dutchieAccountIds,
                order_by: "-score",
                limit: 10000
            },
            abortController: abortController,
        })
    ]).then(([
        dutchieAccounts,
        fetchedRecommendations
    ]) => {
        const productIds = fetchedRecommendations.reduce((ids, rec) => {
            if (rec.discontinued_product_id && !ids.includes(rec.discontinued_product_id && !Product.byId.has(rec.discontinued_product_id))) {
                ids.push(rec.discontinued_product_id);
            }
            if (rec.recommended_product_id && !ids.includes(rec.recommended_product_id) && !Product.byId.has(rec.recommended_product_id)) {
                ids.push(rec.recommended_product_id);
            }
            return ids;
        }, []);

        if (isEmpty(productIds)) {
            return fetchedRecommendations;
        }
        return Promise.all([
            Product.fetch({
                client,
                params: {
                    dutchie_account_id__in: dutchieAccountIds,
                    [Product.productIdField + "__in"]: productIds.join(","),
                },
                abortController: abortController,
                paginate: false,
                cache: shouldCache
            }),
            Package.fetch({
                client,
                params: {
                    dutchie_account_id__in: dutchieAccountIds,
                    [Package.productIdField + "__in"]: productIds.join(","),
                },
                abortController: abortController,
                paginate: false,
                cache: shouldCache
            }),
            Brand.fetch({
                client,
                params: {
                    dutchie_account_id__in: dutchieAccountIds,
                    [Brand.productIdField + "__in"]: productIds.join(","),
                },
                abortController: abortController,
                paginate: false,
                cache: shouldCache
            }),
            Inventory.fetch({
                client,
                params: {
                    dutchie_account_id__in: dutchieAccountIds,
                    [Inventory.productIdField + "__in"]: productIds.join(","),
                },
                abortController: abortController,
                paginate: false,
                cache: shouldCache
            })
        ]).then(([missingProducts, fetchedPackages, fetchedBrands, fetchedInventory]) => {
            return fetchedRecommendations;
        }).catch(err => {
            if (err.code === "ERR_CANCELED") {
                return;
            }
            console.error("Error fetching missing products for procurement: ", err);
        });
    }).catch(err => {
        if (err.code === "ERR_CANCELED") {
            return;
        }
        console.error("Error fetching dutchie data for procurement: ", err);
    });
}


const fetchMetrcData = (client, metrcAccountIds, shouldCache, abortController) => {
    console.log("Getting metrc data...");
    const ninetyDaysAgo = new Date();
    ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
    const today = new Date();

    const selectedIntegrationAccounts = metrcAccountIds.split(",").map(id => {return {id: parseInt(id)};});
    console.log("Metrc account IDs: ", metrcAccountIds, selectedIntegrationAccounts);

    const common = {
        client,
        startDate: ninetyDaysAgo,
        endDate: today,
        abortController,
        selectedIntegrationAccounts: selectedIntegrationAccounts,
        cache: true
    }

    console.log("Getting metrc data in the RetailProcurement view for accounts: ", selectedIntegrationAccounts, "with params", common);

    return Promise.all([
        MetrcProduct.fetchByDate({...common}),
        MetrcBrand.fetchByDate({...common}),
        MetrcInventory.fetchByDate({...common}),
        MetrcPackage.fetchByDate({...common}),
        MetrcPackageWholesalePrice.fetchByDate({...common}),
        InventoryRecommendation.fetch({
            client,
            params: {
                integration: METRC,
                account_id__in: metrcAccountIds,
                order_by: "-score",
                limit: 100
            },
            abortController,
        })
    ]).then(([
        fetchedProducts,
        fetchedBrands,
        fetchedInventory,
        fetchedPackages,
        fetchedWholesalePrices,
        fetchedRecommendations
    ]) => {
        return fetchedRecommendations;
    }).catch(err => {
        console.error("Error fetching metrc data for procurement: ", err);
    });
}


const RetailProcurement = ({organizationId, startDate, endDate}) => {
    const client = new Client();
    const selectedIntegrationAccounts = useSelector(state => state.integrationReducer.accounts.selectedAccounts);
    const [inventoryRecommendations, setInventoryRecommendations] = useState([]);
    const integrationAccountIds = selectedIntegrationAccounts.map(account => account.id).join(",");
    const [shouldCache, setShouldCache] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [productCatalog, setProductCatalog] = useState([]);
    const abortControllerRef = React.useRef(null);
    const selectedIntegration = useSelector(state => state.integrationReducer.selectedIntegration);

    useEffect(() => {
        if (isEmpty(selectedIntegrationAccounts)) {
            return;
        }

        selectedIntegrationAccounts.map(account => {
            if (selectedIntegration === DUTCHIE) return CanonicalIntegrationAccount.fromDutchie(account);
            return CanonicalIntegrationAccount.fromMetrc(account);
        });

        abortControllerRef.current?.abort();
        abortControllerRef.current = new AbortController();

        setIsLoading(true);
        const fetch = selectedIntegration === DUTCHIE ? fetchDutchieData : fetchMetrcData;
        fetch(client, integrationAccountIds, shouldCache, abortControllerRef.current).then(fetchedRecommendations     => {
            if (selectedIntegration === DUTCHIE) {
                client.dutchie.api.v1().get("active-products/", {
                    params: {dutchie_account_id__in: integrationAccountIds}
                }, abortControllerRef.current).then(fetchedCatalog => {
                    setProductCatalog(fetchedCatalog.data.results.map(product => {
                        const dutchieProduct = Product.byId.get(product[0]) || new Product(product);
                        return CanonicalProduct.from(dutchieProduct);
                    }));
                    setInventoryRecommendations(fetchedRecommendations);
                    Model.canonicalize().then(() => {
                        setIsLoading(false);
                    }).catch(err => {
                        setIsLoading(false);
                        console.error("Error canonicalizing models: ", err);
                    });
                });
            } else {
                MetrcProduct.fetch({client, params: {
                    metrc_account_id__in: integrationAccountIds,
                    transactions__receipt__sales_date_time__gte: "1970-01-01",
                    transactions__receipt__sales_date_time__lte: new Date().toISOString().split("T")[0],
                }, paginate: false, cache: shouldCache}).then(fetchedProducts => {
                    Model.canonicalize().then(() => {
                        setProductCatalog(fetchedProducts.map(product => {
                            const metrcProduct = MetrcProduct.byId.get(product.id) || new MetrcProduct(product);
                            return CanonicalProduct.from(metrcProduct)
                        }));
                        setIsLoading(false);
                    }).catch(err => {
                        setIsLoading(false);
                        console.error("Error canonicalizing models: ", err);
                    });
                });
                setIsLoading(false);
            }
        }).catch(err => {
            setIsLoading(false);
            console.error("Error fetching inventory recommendations: ", err);
        });

        return () => {
            abortControllerRef.current?.abort();
        }
    }, [organizationId, selectedIntegrationAccounts]);

    if (isLoading) {
        return <Loading fullscreen={true} title={"Loading product catalog"} />;
    }

    if (isEmpty(selectedIntegrationAccounts)) {
        return <div style={{width: "100%", padding: "20px"}}>No integration accounts selected.</div>;
    }

    const columnDefs = [
        new ColumnDef({displayName: "ID"}),
        new ColumnDef({displayName: "Status"}),
        new ColumnDef({displayName: "Discontinue Product"}),
        new ColumnDef({displayName: "UOH of Product to Discontinue", type: "number"}),
        new ColumnDef({displayName: "Recommended Product"}),
        new ColumnDef({displayName: "UOH of Recommended Product", type: "number"}),
        new ColumnDef({displayName: "Margin Boost"}),
        new ColumnDef({displayName: "Velocity Boost"}),
        new ColumnDef({displayName: "Score", type: "number"}),
    ];

    return <div className={"retail-procurement"}>
        <ProductComparison
            organizationId={organizationId}
            products={productCatalog}
            inventories={Array.from(CanonicalInventory.byId.values())}
            inventoryRecommendations={inventoryRecommendations}
        />
        {isEmpty(inventoryRecommendations) ? null : <InventoryRecommendations
            inventoryRecommendations={inventoryRecommendations}
            columnDefs={columnDefs}
            client={client}
        />}
    </div>
};


export default RetailProcurement;